import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, MessageCircle, Eye, Heart, Link, Leaf, Infinity, Landmark, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import type { Badge } from '@/types/engagement';

const badgeCopy = {
  en: { earned: 'Earned!' },
  es: { earned: '¡Obtenida!' },
  fr: { earned: 'Obtenu !' },
  pt: { earned: 'Conquistada!' },
  ar: { earned: 'حصلت عليها!' },
  ja: { earned: '獲得済み！' },
  ko: { earned: '획득!' },
  id: { earned: 'Diperoleh!' },
};

// Badge key to i18n key mapping
const BADGE_KEY_TO_I18N: Record<string, keyof typeof import('@/i18n/translations').translations.en.howToUse.gamification.jacKeyLines> = {
  first_expression: 'firstExpression',
  relationship_celebrant: 'relationshipCelebrant',
  birthday_champion: 'birthdayChampion',
  friendship_keeper: 'friendshipKeeper',
  consistency_star: 'consistencyStar',
  love_ambassador: 'loveAmbassador',
};

// Map badge icons to Lucide components
const BADGE_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  MessageCircle,
  Eye,
  Heart,
  Link,
  Leaf,
  Infinity,
  Landmark,
  Trophy,
};

// Vibrant colored badge styles matching landing page
const BADGE_STYLES: Record<string, { bg: string; border: string; text: string }> = {
  first_words: {
    bg: 'bg-gradient-to-r from-rose-400 to-pink-500',
    border: 'border-rose-300',
    text: 'text-white',
  },
  first_expression: {
    bg: 'bg-gradient-to-r from-rose-400 to-pink-500',
    border: 'border-rose-300',
    text: 'text-white',
  },
  presence: {
    bg: 'bg-gradient-to-r from-blue-400 to-indigo-500',
    border: 'border-blue-300',
    text: 'text-white',
  },
  love_ambassador: {
    bg: 'bg-gradient-to-r from-purple-400 to-violet-500',
    border: 'border-purple-300',
    text: 'text-white',
  },
  honesty: {
    bg: 'bg-gradient-to-r from-amber-400 to-orange-500',
    border: 'border-amber-300',
    text: 'text-white',
  },
  connection: {
    bg: 'bg-gradient-to-r from-emerald-400 to-green-500',
    border: 'border-emerald-300',
    text: 'text-white',
  },
  care: {
    bg: 'bg-gradient-to-r from-teal-400 to-cyan-500',
    border: 'border-teal-300',
    text: 'text-white',
  },
  birthday_champion: {
    bg: 'bg-gradient-to-r from-yellow-400 to-amber-500',
    border: 'border-yellow-300',
    text: 'text-white',
  },
  friendship_keeper: {
    bg: 'bg-gradient-to-r from-indigo-400 to-blue-500',
    border: 'border-indigo-300',
    text: 'text-white',
  },
  consistency_star: {
    bg: 'bg-gradient-to-r from-fuchsia-400 to-pink-500',
    border: 'border-fuchsia-300',
    text: 'text-white',
  },
  continuity: {
    bg: 'bg-gradient-to-r from-purple-400 to-fuchsia-500',
    border: 'border-purple-300',
    text: 'text-white',
  },
  legacy_builder: {
    bg: 'bg-gradient-to-r from-amber-400 to-yellow-500',
    border: 'border-amber-300',
    text: 'text-white',
  },
  relationship_celebrant: {
    bg: 'bg-gradient-to-r from-pink-400 to-rose-500',
    border: 'border-pink-300',
    text: 'text-white',
  },
};

const DEFAULT_STYLE = {
  bg: 'bg-gradient-to-r from-primary/80 to-primary',
  border: 'border-primary/50',
  text: 'text-white',
};

interface InclusiveBadgeDisplayProps {
  earnedBadges: Badge[];
  unearnedBadges: Badge[];
  className?: string;
}

export function InclusiveBadgeDisplay({
  earnedBadges,
  unearnedBadges,
  className,
}: InclusiveBadgeDisplayProps) {
  const { t, language } = useLanguage();
  const bc = lc(badgeCopy, language);
  const allBadges = [...earnedBadges, ...unearnedBadges];

  // Get JacKey line from i18n
  const getJacKeyLine = (badgeKey: string): string | undefined => {
    const i18nKey = BADGE_KEY_TO_I18N[badgeKey];
    if (i18nKey && t.howToUse.gamification.jacKeyLines[i18nKey]) {
      return t.howToUse.gamification.jacKeyLines[i18nKey];
    }
    return undefined;
  };

  const [activeBadge, setActiveBadge] = useState<{ badge: Badge; earned: boolean } | null>(null);

  return (
    <div className={cn('space-y-3', className)}>
      <h4 className="text-sm font-semibold text-foreground">
        {t.howToUse.gamification.yourBadges} ({earnedBadges.length}/{allBadges.length})
      </h4>
      
      <div className="flex flex-wrap gap-2">
        {earnedBadges.map((badge, index) => {
          const IconComponent = BADGE_ICON_MAP[badge.icon] || Trophy;
          const style = BADGE_STYLES[badge.badge_key] || DEFAULT_STYLE;
          
          return (
            <motion.button
              key={badge.id}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.08, type: 'spring', stiffness: 200 }}
              onClick={() => setActiveBadge({ badge, earned: true })}
              className={cn(
                'relative h-11 w-11 rounded-xl flex items-center justify-center shadow-lg',
                'cursor-pointer transition-transform active:scale-95 hover:scale-110',
                style.bg, style.text
              )}
            >
              <IconComponent className="h-5 w-5" />
              <div className="absolute -top-1 -right-1 h-4 w-4 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                <Check className="h-2.5 w-2.5 text-white" />
              </div>
            </motion.button>
          );
        })}

        {unearnedBadges.map((badge) => {
          const IconComponent = BADGE_ICON_MAP[badge.icon] || Trophy;
          
          return (
            <button
              key={badge.id}
              onClick={() => setActiveBadge({ badge, earned: false })}
              className={cn(
                'h-11 w-11 rounded-xl flex items-center justify-center',
                'bg-muted/70 text-muted-foreground/50 border-2 border-dashed border-muted-foreground/20',
                'cursor-pointer transition-colors active:scale-95 hover:bg-muted'
              )}
            >
              <IconComponent className="h-5 w-5" />
            </button>
          );
        })}
      </div>
      
      <p className="text-xs text-muted-foreground">
        {t.howToUse.gamification.helperMicrocopy}
      </p>

      <AnimatePresence>
        {activeBadge && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="rounded-xl border border-border bg-popover p-4 shadow-lg space-y-2"
          >
            <div className="flex items-center justify-between">
              <p className="font-semibold text-foreground">{activeBadge.badge.name}</p>
              <button
                onClick={() => setActiveBadge(null)}
                className="h-6 w-6 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted active:scale-95"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground">{activeBadge.badge.requirement_description}</p>
            {(() => {
              const jacKeyLine = getJacKeyLine(activeBadge.badge.badge_key);
              if (!jacKeyLine) return null;
              return (
                <>
                  <Separator />
                  <p className="text-xs italic text-primary">JacKey: "{jacKeyLine}"</p>
                </>
              );
            })()}
            {activeBadge.earned && (
              <p className="text-xs text-emerald-600 font-medium">✓ {bc.earned}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
